import { FC, useState, useEffect } from 'react'
import { Link, useParams, useNavigate, useSearchParams } from 'react-router-dom'
import { DashboardContainer } from '@forwardflow/components/Dashboard'
import { Button } from '@forwardflow/ui'
import Icon from '@forwardflow/ui/Icons'
import { getStorageKey } from '@forwardflow/helpers/utils'
import Conversations from '@forwardflow/components/Dashboard/Conversations'
import { DemoLeadList } from '@forwardflow/components/Dashboard/Conversation/DemoLeadList'
import { DemoLead } from '@forwardflow/types/demo-conversation'
import { useDemoConversationStore } from '@forwardflow/store/demoConversationStore'

const DemoConversationsPage: FC = () => {
   const { id } = useParams()
   const navigate = useNavigate()
   const [searchParams] = useSearchParams()
   const [isMenuOpen, setIsMenuOpen] = useState(false)

   const filter = searchParams.get('stages')
   const [selectedLeadData, setSelectedLeadData] = useState<DemoLead | null>(
      null
   )
   const setLeadDetails = useDemoConversationStore(
      (state) => state.setLeadDetails
   )

   const contactFilterOptions = [
      {
         name: 'All Stages',
         value: 'all-stages',
      },
      {
         name: 'Appt. set',
         value: 'ff-appointment-set',
      },
      {
         name: 'Appt. missed',
         value: 'ff-appointment-missed',
      },
   ]

   const getInitialStageFilter = () => {
      if (filter === 'apt-set') return 'ff-appointment-set'
      if (filter === 'apt-missed') return 'ff-appointment-missed'
      return 'all-stages'
   }

   const [selectedStageFilter, setSelectedStageFilter] = useState<string>(
      getInitialStageFilter()
   )

   const [selectedContactLeadId, setSelectedContactLeadId] = useState<
      number | null
   >(id ? Number(id) : null)

   const DEMO_CAMPAIGN_NAME = 'Demo Source'

   // sets the initial selected stage filter based on URL params
   useEffect(() => {
      const newStageFilter = getInitialStageFilter()
      if (newStageFilter !== selectedStageFilter) {
         setSelectedStageFilter(newStageFilter)
      }
   }, [filter])

   // sets the initial selected lead ID based on URL params
   useEffect(() => {
      const currentLeadId = id ? Number(id) : null
      if (currentLeadId !== selectedContactLeadId) {
         setSelectedContactLeadId(currentLeadId)

         if (currentLeadId === null) {
            setSelectedLeadData(null)
         }
      }
   }, [id])

   const handleStageFilterSelect = (stage: string) => {
      setSelectedStageFilter(stage)

      let newSearchFilter = 'all-stages'
      if (stage === 'ff-appointment-set') {
         newSearchFilter = 'apt-set'
      } else if (stage === 'ff-appointment-missed') {
         newSearchFilter = 'apt-missed'
      }

      const params = new URLSearchParams()
      if (newSearchFilter !== 'all-stages') {
         params.set('stages', newSearchFilter)
      }

      const queryString = params.toString()
      const urlSuffix = queryString ? `?${queryString}` : ''

      navigate(`/demo-conversations${urlSuffix}`)

      setSelectedContactLeadId(null)
      setSelectedLeadData(null)
   }

   const handleLeadSelect = (leadId: number | null, leadData?: DemoLead) => {
      setSelectedContactLeadId(leadId)
      setSelectedLeadData(leadData || null)

      if (leadId !== null && leadData) {
         const existingLeadDetails = useDemoConversationStore
            .getState()
            .getLeadDetails(leadId)
         if (!existingLeadDetails) {
            setLeadDetails(leadId, leadData)
         }

         const storageKey = getStorageKey(
            selectedStageFilter,
            DEMO_CAMPAIGN_NAME
         )
         localStorage.setItem(storageKey, leadId.toString())
      }
   }

   const getLeadDetailsForConversation = () => {
      if (selectedContactLeadId === null || !selectedLeadData) {
         return null
      }

      const transformedData: any = {
         tags: selectedLeadData.tags || [],
         leadInterestType: selectedLeadData.leadInterestType || 'demo-seller',
         userInfo: {
            id: selectedLeadData.userInfo.id,
            email: selectedLeadData.userInfo.email,
            phone: selectedLeadData.userInfo.phone,
            lastName: selectedLeadData.userInfo.lastName,
            firstName: selectedLeadData.userInfo.firstName,
            avatar: selectedLeadData.userInfo.avatar || null,
         },
         assistant: {
            avatar: 'PLACEHOLDER',
            name: 'PLACEHOLDER',
         },
         agentInfo: {
            id: selectedLeadData.agentInfo?.id?.toString(),
            name: selectedLeadData.agentInfo?.name,
         },
         stageInfo: {
            id: selectedLeadData.stageInfo.id.toString(),
            name: selectedLeadData.stageInfo.name,
            updatedAt: new Date().toISOString(),
         },
         sourceInfo: {
            id: selectedLeadData.sourceInfo?.id || 0,
            name: selectedLeadData.sourceInfo?.name,
            addedAt: new Date().toISOString(),
         },
         leadSummary: selectedLeadData.timelineSummary,
         questions: selectedLeadData.questions,
         appointmentInfo: {
            date: selectedLeadData.appointmentInfo?.date || '',
            status: selectedLeadData.appointmentInfo?.status || 'Scheduled',
            platform: selectedLeadData.appointmentInfo?.platform || 'Zoom',
         },
         conversationInfo: {
            messagesSent: selectedLeadData.conversationInfo?.messagesSent || 0,
            callsAnswered:
               selectedLeadData.conversationInfo?.callsAnswered || 0,
            callsAttempted:
               selectedLeadData.conversationInfo?.callsAttempted || 0,
            messagesReplied:
               selectedLeadData.conversationInfo?.messagesReplied || 0,
         },
         propertyInfo: selectedLeadData.propertyInfo,
         preDefinedTimelineHistory:
            selectedLeadData.preDefinedTimelineHistory || [],
         assistantInfo: {
            id: selectedLeadData?.assistantInfo?.id || 0,
            name: selectedLeadData?.assistantInfo?.name,
         },
      }

      return transformedData
   }

   return (
      <>
         <style>{`html { overflow: hidden; }`}</style>
         <DashboardContainer>
            {/* Header */}
            <div className="bg-white/5 min-h-[52px] rounded-lg flex items-center mb-4">
               <Link to="/dashboard">
                  <Button className="!w-[36px] h-[36px] border border-transparent bg-transparent !rounded-lg !mt-0 ml-2 mr-4">
                     <Icon
                        name="arrow-right"
                        className="rotate-180 fill-white"
                        width={20}
                        height={20}
                     />
                  </Button>
               </Link>
               <span className="text-[#717171]">
                  <Link to="/dashboard"> Home /</Link>
                  <span className="text-white"> Demo Conversations</span>
               </span>

               {/* Stage Filter Options */}
               <div className="flex flex-wrap items-center gap-2 pr-2 ml-auto">
                  {/* Mobile hamburger menu */}
                  <div className="relative lg:hidden">
                     <Button
                        onClick={() => setIsMenuOpen(!isMenuOpen)}
                        className="!border-white/20 !bg-transparent !w-[38px] !h-[38px] !p-2 !mt-0 !rounded-lg"
                     >
                        <Icon name="Options" className="scale-[1] fill-white" />
                     </Button>

                     {/* Mobile dropdown */}
                     {isMenuOpen && (
                        <div className="absolute right-0 z-50 bg-[#303137] rounded-lg shadow-lg px-4 pb-2 mt-2 min-w-[150px]">
                           {contactFilterOptions?.map((option) => (
                              <Button
                                 key={option.value}
                                 onClick={() => {
                                    handleStageFilterSelect(option.value)
                                    setIsMenuOpen(false)
                                 }}
                                 className={`!bg-transparent text-white/70 w-full border-[#828282] !text-[14px] whitespace-nowrap text-left px-4 !h-auto !py-2 hover:bg-[#404147] ${
                                    selectedStageFilter === option.value
                                       ? 'text-white bg-[#404147]'
                                       : ''
                                 }`}
                              >
                                 {option.name}
                              </Button>
                           ))}
                        </div>
                     )}
                  </div>

                  {/* Desktop filter buttons */}
                  <div className="hidden lg:flex lg:items-center lg:gap-2">
                     {contactFilterOptions?.map((option) => {
                        const isSelected = selectedStageFilter === option.value
                        return (
                           <Button
                              key={option.value}
                              onClick={() =>
                                 handleStageFilterSelect(option.value)
                              }
                              className={`text-white/70 group !w-auto hover:text-white hover:!bg-primary/20 !text-[14px] !font-medium whitespace-nowrap !h-[36px] !mt-0 px-[30px] !rounded-lg flex items-center ${
                                 isSelected
                                    ? 'text-white !bg-primary/20 !border-none'
                                    : '!bg-transparent !border-white/20'
                              }`}
                           >
                              {option.name}
                           </Button>
                        )
                     })}
                  </div>

                  {/* Demo Source Badge (instead of campaign selector) */}
                  <div className="px-4 py-2 text-sm font-medium text-white border rounded-lg bg-primary/20 border-primary/30">
                     Demo Source
                  </div>
               </div>
            </div>

            {/* Main Content */}
            <div className="flex flex-col w-full gap-5 lg:flex-row max-h-[calc(100%-80px)]">
               <DemoLeadList
                  onSelectLead={handleLeadSelect}
                  selectedStageFilter={selectedStageFilter}
               />
               <div className="flex-1 flex-grow">
                  <Conversations
                     selectedLeadDetails={getLeadDetailsForConversation()}
                     isLoading={false}
                  />
               </div>
            </div>
         </DashboardContainer>
      </>
   )
}

export default DemoConversationsPage
